import { Location } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    inject,
    signal,
} from '@angular/core';

import { IAEMCarousel, ICMSBannerConfig, IOffer, IOfferReport, IParams } from '@frontend/oxygen/core-utils';
import { RememberMeService as RememberMeStatusService } from '@frontend/vanilla/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, first, map, switchMap } from 'rxjs/operators';
import { isEmpty } from 'underscore';

import { LAZY_LOAD_ROUTE_PATHS } from '../../../bma/constants/lazyload-route-paths.constant';
import { EnvironmentConfig } from '../../../client-config/index';
import { IGtmBannerEvent } from '../../../core/models/gtm.event.model';
import { BannerClickService } from '../../../core/services/aemBanners/banner-click.service';
import { BannersService } from '../../../core/services/aemBanners/banners.service';
import { BANNERS } from '../../../core/services/aemBanners/enums/banners.service.enums';
import { Utils } from '../../../core/services/aemBanners/utils';
import { BRANDS_FOR_AEM, DEFAULT_OPTIONS, dynamicBannersPageMap } from '../../../core/services/aemBanners/utils';
import { CmsService } from '../../../core/services/cms/cms.service';
import { ISystemConfig } from '../../../core/services/cms/models';
import { PubSubService } from '../../../core/services/communication/pubsub/pubsub.service';
import { DeviceService } from '../../../core/services/device/device.service';
import { DomToolsService } from '../../../core/services/domTools/dom.tools.service';
import { GtmService } from '../../../core/services/gtm/gtm.service';
import { StorageService } from '../../../core/services/storage/storage.service';
import { UserService } from '../../../core/services/user/user.service';
import { WindowRefService } from '../../../core/services/windowRef/window-ref.service';
import { NO_BANNER_SPORTS } from '../../../shared/constants/sports.constant';
import { Carousel } from '../../../shared/directives/ng-carousel/carousel.class';
import { CarouselService } from '../../../shared/directives/ng-carousel/carousel.service';
import { RendererService } from '../../../shared/services/renderer/renderer.service';
import { AWSFirehoseService } from '../../awsFirehose/service/aws-firehose.service';
import { FEATURE_FLAGS } from '../../flagSource/constants/flag-source.constants';

@Component({
    selector: 'banners-section',
    templateUrl: 'banners-section.component.html',
    styleUrls: ['styles/banners-section.component.scss', 'styles/lc-carousel.scss'],
    providers: [BannersService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BannersSectionComponent implements OnInit, OnDestroy, AfterViewInit {
    _pagevalue: string;
    offers: IOffer[];
    allOffers: IOffer[];
    brand: string = '';
    isBannerSectionEmpty: boolean = false;
    bannersVisibility: string = 'hidden';
    showBanners: boolean = true;
    carouselName: string = 'aem-banners-carousel';
    isBannersEnabled: boolean = false;
    firstImageLoaded: boolean = false;
    isLoaded: boolean = false;
    isSlided: boolean = false;
    eargerLoadCountMobile: number;
    eargerLoadCountDesktop: number;
    fgSizes = this.bannerService?.bannersConfig?.SIZES_MOBILE_FOREGROUND ?? '';
    bgSizes = this.bannerService?.bannersConfig?.SIZES_MOBILE_BACKGROUND ?? '';
    srcSet = (this.bannerService?.bannersConfig?.SRCSET_MOBILE ?? []).map((v) => `${v}w`).join(',');
    showDynamicBanners = signal<boolean>(false);

    public timePerSlide: number = 7000;

    readonly SWIPER_RESET_TIMEOUT = 3;

    public activeSlideIndex: number = 0;

    public carousel: IAEMCarousel;

    public animatedPictureRegex: RegExp = /(.+)\/(content)\/(.+)/;

    // GA Tracking properties
    private isGTMSuccess: boolean = false;

    private readonly LIVE_ODDS_INTERVAL: number = 10000;

    private orientationChangeListener: any;

    private cmsBannerConfig: ICMSBannerConfig;

    private element: HTMLElement;

    private reloadTimer: number;
    @Input() set page(value: string) {
        this._pagevalue = value;
        this.getTeasersData();
    }
    get page() {
        return this._pagevalue;
    }
    private readonly environment = inject(EnvironmentConfig).configuration;
    private reloadInterval: number;

    @Output() bannersLoaded = new EventEmitter<boolean>();

    constructor(
        protected wr: WindowRefService,
        protected pubsub: PubSubService,
        protected ds: DeviceService,
        protected ss: StorageService,
        protected gtm: GtmService,
        protected us: UserService,
        protected bannerService: BannersService,
        protected rendererService: RendererService,
        protected carouselService: CarouselService,
        protected cmsService: CmsService,
        protected elementRef: ElementRef,
        protected dts: DomToolsService,
        protected bannerClickService: BannerClickService,
        protected changeDetectorRef: ChangeDetectorRef,
        protected location: Location,
        protected rm: RememberMeStatusService,
        protected aws: AWSFirehoseService,
    ) {
        this.brand = Utils.resolveBrandOrDefault(this.environment.BRAND);
        this.element = this.elementRef.nativeElement;
        this.handleReload = this.handleReload.bind(this);
    }

    ngOnInit(): void {
        this.reloadInterval = this.wr.nativeWindow.setInterval(() => {
            this.getLaterUpdates();
        }, this.LIVE_ODDS_INTERVAL);
    }
    /*
     * Gets offers Odds immediatly after rendering banners
     * */
    ngAfterViewInit() {
        this.getLaterUpdates();
    }
    /*
     * Gets offers Odds for configured time interval
     * */
    getLaterUpdates() {
        if (this.allOffers && this.allOffers.length > 0) {
            this.offers = this.allOffers
                .map((offer: IOffer) => this.bannerService.getOdds(offer))
                .filter((offer: IOffer) => offer.bannerStatus === true);
        }
    }

    isIrishUser(): boolean {
        return this.us.countryCode === 'IE' || this.ss.get('countryCode') === 'IE';
    }

    gotToSlide(index: number): void {
        this.bannersCarousel.toIndex(index);
    }
    applyConfigFromCMS(config: ICMSBannerConfig) {
        this.cmsBannerConfig = config;
        this.isBannersEnabled = this.cmsBannerConfig.enabled;
        this.cmsService.isDynamicBannersEnabled.set(this.isBannersEnabled);
        if (!this.isBannersEnabled) {
            this.isBannersEnabled = false;
            this.dts.css(this.element!.parentNode!.parentElement!, 'contain-intrinsic-size', '0');
            this.cmsService.isDynamicBannersEnabled.set(this.isBannersEnabled);
        }

        if (this.cmsBannerConfig.timePerSlide && this.cmsBannerConfig.timePerSlide > 0) {
            // convert seconds to milliseconds
            this.timePerSlide = this.cmsBannerConfig.timePerSlide * 1000;
        }
    }

    ngOnDestroy(): void {
        this.orientationChangeListener && this.orientationChangeListener();
        this.unsubscribeFromEvents();
        this.wr.nativeWindow.clearTimeout(this.reloadTimer);
        this.wr.nativeWindow.clearInterval(this.reloadInterval);
    }

    actionHandler($event: MouseEvent, offer: IOffer): void {
        $event.preventDefault();
        this.trackClickGTMEvent($event);
        this.bannerClickService.handleBannerClick($event, offer, false);
    }

    handleActiveSlide(slideIndex: number): void {
        if (!Number.isInteger(slideIndex)) {
            return;
        }
        if (!this.isSlided) {
            this.isSlided = true;
        }
        const i = slideIndex - 1;

        if (this.offers[i]) {
            this.offers[this.activeSlideIndex].active = false;
            this.offers[i].active = true;
            this.activeSlideIndex = i;
            this.changeDetectorRef.detectChanges();
        }
        this.trackBannerView();
    }

    resetSwiper(offers: IOffer[]): void {
        this.findGifsAndLoadInBackground(offers);
        this.allOffers = offers;
        this.offers = offers.filter((offer: IOffer) => offer.bannerStatus === true);

        offers[0].active = true;
        //Adding fetch Priority to reduce LCP time
        offers[0].fetchpriority = 'high';
        offers.length > 1 && (offers[1].fetchpriority = 'high');

        this.activeSlideIndex = 0;
        this.bannersVisibility = 'visible';
        this.trackBannerView();
    }

    findGifsAndLoadInBackground(offers: IOffer[]): void {
        offers.forEach((offer: IOffer) => {
            if (offer?.imgUrl && this.animatedPictureRegex.test(offer.imgUrl)) {
                // set plain image until gif will be loaded
                const originalGif = offer.imgUrl;
                const pictureBasedOnGif = originalGif.replace(this.animatedPictureRegex, '$1/image/$3');
                offer.imgUrl = pictureBasedOnGif;

                // load animated image in background..
                this.loadImageInBackgroundForOffer(offer, originalGif!);
            }
        });
    }

    loadImageInBackgroundForOffer(offer: IOffer, originalGif: string): HTMLImageElement {
        const animatedImage = new Image();
        animatedImage.src = originalGif;
        animatedImage.onload = () => {
            offer.imgUrl = originalGif;
        };
        return animatedImage;
    }

    onFirstImageLoaded(): void {
        this.firstImageLoaded = true;
    }

    removeSlideOnError(index: number): void {
        this.offers.splice(index, 1);
    }

    nextSlide(): void {
        this.bannersCarousel.next();
        this.sliderGTMTracker('right');
    }

    prevSlide(): void {
        this.bannersCarousel.previous();
        this.sliderGTMTracker('left');
    }

    public initPubSubAndSubscriptions(): void {
        this.pubsub.publish(this.pubsub.API.IS_BANNER_SECTION_AVAILABLE, [isEmpty(this.offers)]);

        this.pubsub.subscribe('dynamicBanners', this.pubsub.API.RELOAD_COMPONENTS, this.handleReload);
        this.isBannerSectionEmpty = isEmpty(this.offers);

        this.changeDetectorRef.markForCheck();
    }

    /**
     *
     * @param bannersCount
     * @return void
     * Log warning message in case sport is nonbanner sport and banner is enabled
     */
    private addLogsForNonBannerSport(bannersCount: any): void {
        if (bannersCount && this.isNonBannerSport()) {
            this.aws.addAction(
                `Banners=>CLS logs=>Warning Message:, 'Non banner sports supports no banners. Do not configure banners for these sports as CLS may vary.'`,
            );
        }
    }

    public requestOffersAndCarouselInit(): Observable<any> {
        return this.bannerService.fetchOffersFromAEM(this.carousel.settings).pipe(
            map((response: IOfferReport) => {
                this.showBanners = response.offers.length > 0;
                this.addLogsForNonBannerSport(this.showBanners);
                this.bannersLoaded.emit(this.showBanners);
                if (this.showBanners) {
                    this.resetSwiper(response.offers);
                } else {
                    this.dts.css(this.element, 'display', 'none');
                    this.dts.css(this.element, 'height', '0');
                    this.dts.css(this.element!.parentNode!.parentElement!, 'contain-intrinsic-size', '0');
                    this.dts.css(this.element!.parentNode!.parentElement!, 'min-height', '0');
                }
                this.changeDetectorRef.markForCheck();
            }),
            catchError((error) => {
                this.isBannersEnabled = false;
                this.showBanners = false;
                return throwError(error);
            }),
            finalize(() => (this.isLoaded = true)),
        );
    }

    public trackBannerView(): void {
        const activeOffer = this.offers[this.activeSlideIndex];

        if (!activeOffer.tracked) {
            this.gtm.push('trackEvent', {
                eventCategory: 'banner',
                eventAction: 'view',
                eventLabel: activeOffer.itemName,
                personalised: activeOffer.personalised,
                location: this.wr.nativeWindow.location.pathname,
                vipLevel: this.us.vipLevel || null,
                position: activeOffer.position,
            });
            activeOffer.tracked = true;
        }
    }

    /**
     * AEM Banners GA tracking logic by collecting info about slide (offer) clicked
     * @param {MouseEvent} event
     */
    public trackClickGTMEvent(event: MouseEvent): void {
        if (!this.offers.length || this.isGTMSuccess) {
            return;
        }
        const banner = this.offers[this.activeSlideIndex];

        const vipLevel: string = this.us.vipLevel || null;
        const gtmEvent: IGtmBannerEvent = {
            event: 'trackEvent',
            eventCategory: 'banner',
            eventAction: 'click',
            eventLabel: banner.itemName,
            personalised: banner.personalised,
            location: this.wr.nativeWindow.location.pathname,
            position: banner.position ? banner.position : this.activeSlideIndex + 1,
            vipLevel: vipLevel,
        };

        this.gtm.push('aemBannerClick', gtmEvent);
        // Set true as we need to send GTM one time
        this.isGTMSuccess = true;
    }

    /*
     * Update dynamic banners with new properties
     * @param {object} params
     * */
    public updateDynamicBanners(): void {
        const options = {
            userType: this.getUserType(),
        };

        const vipLevel = this.ss.get('vipLevel');
        if (vipLevel) {
            (options as any)['imsLevel'] = vipLevel;
        }

        this.carousel._options = Utils.assign({}, this.carousel._options, options);
        this.carousel.settings = Utils.assign({}, DEFAULT_OPTIONS, this.carousel._options);

        this.requestOffersAndCarouselInit()
            .pipe(first())
            .subscribe(
                () => {},
                () => {},
            );
    }
    /**
     * Check if the current url is five-a-side
     * @returns {boolean}
     */
    public checkForFiveASideUrl(): boolean {
        const currentPath: string = this.location.path();
        return currentPath.includes(LAZY_LOAD_ROUTE_PATHS.fiveASideShowdown);
    }

    /*
     * Get user type
     * @return {string}
     * */
    private getUserType(): string {
        const user = this.ss.get('existingUser');
        if (!user) {
            return this.brand === BRANDS_FOR_AEM.ladbrokes ? 'new' : 'anonymous';
        } else {
            return this.us.isRetailUser() ? this.us.accountBusinessPhase : 'existing';
        }
    }

    private getTeasersData(): void {
        this.clearStorage();
        this.subscribeForEvents();
        this.cmsService
            .getSystemConfig()
            .pipe(
                switchMap((config: ISystemConfig) => {
                    if (config && config.DynamicBanners) {
                        this.applyConfigFromCMS(config.DynamicBanners);
                    }
                    if (config && config.EagerLoadImagesNumber) {
                        this.eargerLoadCountDesktop = config.EagerLoadImagesNumber.SiteCoreBannerDesktop;
                        this.eargerLoadCountMobile = config.EagerLoadImagesNumber.SiteCoreBannerMobile;
                    }
                    if (this.isBannersEnabled) {
                        const _options = this.getBannerDynamicParams();
                        const settings = Utils.assign({}, DEFAULT_OPTIONS, _options);
                        this.carousel = { _options, settings };
                        this.initOrientationChangeListener();
                        return this.requestOffersAndCarouselInit().pipe(
                            finalize(() => {
                                this.bannersLDHandler();
                                this.initPubSubAndSubscriptions();
                            }),
                            first(),
                        );
                    } else {
                        return of({});
                    }
                }),
            )
            .subscribe(
                () => {},
                () => {},
            );
    }
    private initOrientationChangeListener(): void {
        this.orientationChangeListener = this.rendererService.renderer.listen(this.wr.nativeWindow, 'orientationchange', () => {
            this.showBanners = false;
            this.wr.nativeWindow.setTimeout(() => {
                this.showBanners = this.offers.length > 0;
                this.resetSwiper(this.offers.slice());
            }, this.SWIPER_RESET_TIMEOUT);
        });
    }

    /**
     * Generates params needed for dynamic banner
     * @return {object}
     */
    private getBannerDynamicParams(): IParams {
        const baseParams: IParams = {
            locale: this.isIrishUser() && this.brand === BRANDS_FOR_AEM.ladbrokes ? 'en-ie' : 'en-gb',
            brand: this.brand,
            channel: this.getAppType(),
            userType: this.getUserType(),
        };
        baseParams.page = (dynamicBannersPageMap as any)[this!.page!] ? (dynamicBannersPageMap as any)[this!.page!] : this.page;

        const vipLevel = this.ss.get('vipLevel');
        // This check is needed for requirement: if no vip level then do not send this key at all
        if (vipLevel) {
            baseParams.imsLevel = vipLevel;
        }

        if (this.ds.viewType !== 'desktop') {
            baseParams.device = 'mobile';
        }

        if (this.cmsBannerConfig.maxOffers && this.cmsBannerConfig.maxOffers > 0) {
            baseParams.maxOffers = this.cmsBannerConfig.maxOffers;
        }

        return baseParams;
    }

    /**
     * Subscribes for events (like login, logout) to rerender banner
     * @param {object} params
     */
    private subscribeForEvents(): void {
        this.pubsub.subscribe('dynamicBanners', this.pubsub.API.SESSION_LOGIN, () => {
            if (this.carousel) {
                this.ss.remove('teasersData');
                this.changeDetectorRef.markForCheck();
                this.showBanners = false;
                // Timeout added to get ngx-swiper-wrapper ready to update slides
                this.wr.nativeWindow.setTimeout(() => {
                    this.updateDynamicBanners();
                }, this.SWIPER_RESET_TIMEOUT);
            }
        });
    }

    /**
     * Unsubscribe from events
     */
    private unsubscribeFromEvents(): void {
        this.pubsub.unsubscribe('dynamicBanners');
    }

    /*
     * Get application type
     * @return {string}
     * */
    private getAppType(): string {
        if (this.page === 'retail') {
            return 'connect'; // TODO: rename to retail after changes in cms.
        } else if (this.ds.isWrapper) {
            return 'app';
        } else if (this.ds.isMobile || this.ds.isTablet || this.ds.isMobileOrigin) {
            return 'mobile';
        } else if (this.ds.isDesktop) {
            return 'mobile'; // temporary added, until we will have 'desktop' channel
        }
        return '';
    }

    private get bannersCarousel(): Carousel {
        return this.carouselService.get(this.carouselName)! as unknown as Carousel;
    }
    private set bannersCarousel(value: Carousel) {}

    private handleReload(): void {
        this.wr.nativeWindow.clearTimeout(this.reloadTimer);
        this.reloadTimer = this.wr.nativeWindow.setTimeout(() => {
            this.ngOnDestroy();
            this.ngOnInit();
            this.subscribeForEvents();
        }, DEFAULT_OPTIONS.atJsLoadingTimeout);
    }

    /**
     * AEM Banners GA tracking logic by collecting info about slide (offer) clicked on right and left button
     * @param {string} slideDirection
     */
    private trackSliderClickGTMEvent(slideDirection: string): void {
        const gtmData = {
            'event': 'Event.Tracking',
            'component.CategoryEvent': 'banner',
            'component.LabelEvent': 'virtuals',
            'component.ActionEvent': 'click',
            'component.PositionEvent': 'top banner',
            'component.LocationEvent': 'not applicable',
            'component.EventDetails': `navigate ${slideDirection}`,
            'component.URLclicked': 'not applicable',
        };
        this.gtm.push(gtmData.event, gtmData);
    }

    /**
     * GAtracking handler for sports on left and right slider
     * @param {string} slideDirection
     */
    private sliderGTMTracker(slideDirection: string): void {
        if (this.page === 'virtuals') {
            this.trackSliderClickGTMEvent(slideDirection);
        }
    }

    /**
     * ensure teasers call should fire for 2nd time based on storage object data.
     * 1. rememberMe case - teaser call from preload.js fired without token (as clientconfig has no xsrf-token)
     * and token value retrieved later in partial config call
     */
    clearStorage() {
        this.cmsService.getSystemConfig().subscribe((config: ISystemConfig) => {
            if (config?.userLogs?.names && config?.userLogs?.names.includes(this.us?.username)) {
                this.aws.addAction(`Banners=>logs=>tcwt value:, ${this.rm.tokenExists()}`);
            }
        });
        if (this.rm.tokenExists()) {
            const clientConfig = (this.wr.nativeWindow && this.wr.nativeWindow.clientConfig) || {};
            const tokenvalue =
                (clientConfig && clientConfig.vnUser && clientConfig.vnUser['xsrfToken'] && clientConfig.vnUser['xsrfToken'].toString()) || '';
            if (tokenvalue && tokenvalue.length > 0) {
                this.ss.remove('teasersData');
            }
        }
    }

    private isNonBannerSport(): boolean {
        return NO_BANNER_SPORTS.some((sportName: string) => this.wr.nativeWindow?.location?.pathname?.includes(sportName));
    }

    /**
     * To set showDynamicBanners value
     */
    setShowBannersLD(): void {
        this.showDynamicBanners.set(this.isBannersEnabled && this.bannerService.getLatestVariation(this.wr, this.page));
        this.cmsService.isDynamicBannersEnabled.set(this.showDynamicBanners());
        this.changeDetectorRef.markForCheck();
    }

    /**
     * To get the launch Darkly flag updates for sportBook Banners
     */
    bannersLDHandler(): void {
        this.setShowBannersLD();
        this.pubsub.subscribe(
            BANNERS.dynamicBanners,
            [this.pubsub.API.FLAG_LD_INIT, `FLAG_UPDATE_${FEATURE_FLAGS.SportsbookBanners}`, this.pubsub.API.FLAG_IDENTIFY_INIT],
            () => {
                this.setShowBannersLD();
            },
        );
    }
}
